import React from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Theme,
    makeStyles,
    Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'react-feather';
import { CapitalizeNames } from 'src/utils/capitalize';
import { IMake } from 'src/interfaces/make';
import { IVehicle } from 'src/interfaces/vehicle';
import { getVehicleYearsForSelect } from 'src/constants/vehicles/years';
import {
    TServiceClientFormVehicle,
    emptyVehicleValues,
} from './types';

const useStyles = makeStyles((theme: Theme) => ({
    vehicleRow: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
    },
    addVehicleButton: {
        marginTop: theme.spacing(1),
    },
}));

export interface IServiceClientVehiclesCardProps {
    vehicles: TServiceClientFormVehicle[];
    onVehiclesChange: (vehicles: TServiceClientFormVehicle[]) => void;
    onBlur: (e: React.FocusEvent<unknown>) => void;
    vehiclesByMake: Record<string, IVehicle[]>;
    onFetchVehiclesForMake: (makeId: string) => void;
    onClearVehicles: () => void;
    makes: IMake[] | undefined;
    currentYear: number;
}

export const ServiceClientVehiclesCard = ({
    vehicles,
    onVehiclesChange,
    onBlur,
    vehiclesByMake,
    onFetchVehiclesForMake,
    onClearVehicles,
    makes,
    currentYear,
}: IServiceClientVehiclesCardProps) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const yearRange = getVehicleYearsForSelect(currentYear - 10, currentYear + 1);
    const minVehicleYear = yearRange.length > 0 ? yearRange[yearRange.length - 1] : String(currentYear);
    const maxVehicleYear = yearRange.length > 0 ? yearRange[0] : String(currentYear + 1);

    const handleMakeChange = (index: number, makeId: string) => {
        const makeName = makes?.find((m) => m._id === makeId)?.name ?? '';
        const next = [...vehicles];
        next[index] = {
            ...(next[index] || emptyVehicleValues),
            make: makeId,
            makeName,
            vehicleId: '',
            model: '',
            modelType: '',
            year: '',
            vin: '',
            kilometers: 0,
        };
        onVehiclesChange(next);
        if (makeId) onFetchVehiclesForMake(makeId);
        else onClearVehicles();
    };

    const handleModelSelect = (
        index: number,
        v: IVehicle & { year?: string; vin?: string; kilometers?: number }
    ) => {
        const next = [...vehicles];
        next[index] = {
            ...(next[index] || emptyVehicleValues),
            make: v.make?._id ?? next[index]?.make ?? '',
            makeName: v.make?.name ?? next[index]?.makeName ?? '',
            vehicleId: v._id ?? '',
            model: v.model ?? '',
            modelType: v.modelType ?? '',
            year: v.year ?? '',
            vin: v.vin ?? '',
            kilometers: v.kilometers ?? 0,
        };
        onVehiclesChange(next);
    };

    const updateVehicleField = (
        index: number,
        field: 'make' | 'makeName' | 'vehicleId' | 'model' | 'modelType' | 'year' | 'vin' | 'kilometers',
        value: string | number
    ) => {
        const next = [...vehicles];
        next[index] = {
            ...(next[index] || emptyVehicleValues),
            [field]: value,
        };
        onVehiclesChange(next);
    };

    const addVehicle = () => {
        onVehiclesChange([...vehicles, { ...emptyVehicleValues }]);
    };

    const removeVehicle = (index: number) => {
        onVehiclesChange(vehicles.filter((_, i) => i !== index));
    };

    return (
        <Card variant="outlined" data-testid="service-client-vehicles-card">
            <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="h4" data-testid="vehicles-card-title">{String(t('serviceClients.vehicle'))}</Typography>
                    <Button
                        data-testid="vehicles-card-add-button"
                        size="small"
                        color="primary"
                        variant="outlined"
                        startIcon={<Plus size={16} />}
                        onClick={addVehicle}
                        className={classes.addVehicleButton}
                    >
                        {String(t('serviceClients.AddVehicle'))}
                    </Button>
                </Box>

                {vehicles.map((vehicle, index) => {
                    const makeId = vehicle.make;
                    const optionsForMake = makeId ? (vehiclesByMake[makeId] ?? []) : [];
                    const selectedVehicleFromApi = optionsForMake.find(
                        (v) => v.model === vehicle.model && v.make?._id === makeId
                    );

                    return (
                        <Box key={index} className={classes.vehicleRow} data-testid={`vehicle-row-${index}`}>
                            <Box display="flex" justifyContent="flex-end" mb={1}>
                                <IconButton
                                    data-testid={`vehicle-row-delete-${index}`}
                                    size="small"
                                    onClick={() => removeVehicle(index)}
                                    title={String(t('Buttons.Delete'))}
                                    aria-label={String(t('Buttons.Delete'))}
                                >
                                    <Trash2 size={18} />
                                </IconButton>
                            </Box>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel>{String(t('serviceClient.Make'))}</InputLabel>
                                        <Select
                                            data-testid={`vehicle-row-make-${index}`}
                                            value={vehicle.make}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                handleMakeChange(index, typeof val === 'string' ? val : '');
                                            }}
                                            onBlur={onBlur}
                                            label={String(t('serviceClient.Make'))}
                                        >
                                            <MenuItem value="">
                                                <em>{String(t('Messages.NoneO'))}</em>
                                            </MenuItem>
                                            {(makes || []).map((m: IMake) => (
                                                <MenuItem key={m._id} value={m._id}>
                                                    {CapitalizeNames(m.name)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel>{String(t('serviceClient.Model'))}</InputLabel>
                                        <Select
                                            data-testid={`vehicle-row-model-${index}`}
                                            value={
                                                selectedVehicleFromApi?._id ?? (vehicle.model || '')
                                            }
                                            onChange={(e) => {
                                                const idOrModel = e.target.value;
                                                const v = optionsForMake.find(
                                                    (x) =>
                                                        x._id === idOrModel || x.model === idOrModel
                                                );
                                                if (v) handleModelSelect(index, v);
                                                else updateVehicleField(index, 'model', String(idOrModel));
                                            }}
                                            onBlur={onBlur}
                                            label={String(t('serviceClient.Model'))}
                                            displayEmpty
                                        >
                                            {optionsForMake.map((v: IVehicle) => (
                                                <MenuItem key={v._id} value={v._id}>
                                                    {CapitalizeNames(v.make?.name)} {v.model}
                                                </MenuItem>
                                            ))}
                                            {vehicle.model &&
                                                !optionsForMake.some((v) => v.model === vehicle.model) && (
                                                    <MenuItem value={vehicle.model}>
                                                        {vehicle.model}
                                                    </MenuItem>
                                                )}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        disabled
                                        label={String(t('serviceClient.ModelType'))}
                                        value={vehicle.modelType}
                                        onChange={(e) =>
                                            updateVehicleField(index, 'modelType', e.target.value)
                                        }
                                        onBlur={onBlur}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        label={String(t('serviceClient.Year'))}
                                        value={vehicle.year}
                                        onChange={(e) =>
                                            updateVehicleField(index, 'year', e.target.value)
                                        }
                                        onBlur={onBlur}
                                        inputProps={{
                                            min: minVehicleYear,
                                            max: maxVehicleYear,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        label={String(t('serviceClient.Kilometers'))}
                                        type="number"
                                        value={vehicle.kilometers || ''}
                                        onChange={(e) =>
                                            updateVehicleField(
                                                index,
                                                'kilometers',
                                                Number(e.target.value) || 0
                                            )
                                        }
                                        onBlur={onBlur}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        label={String(t('serviceClient.Vin'))}
                                        value={vehicle.vin}
                                        onChange={(e) =>
                                            updateVehicleField(index, 'vin', e.target.value)
                                        }
                                        onBlur={onBlur}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    );
                })}
            </CardContent>
        </Card>
    );
};
